import { Box, Table, Tooltip } from "@procore/core-react";

import { useDbStatus } from "hooks/useDbStatus";
import { getPropertyDisplayName, getPropertyDisplayValue } from "utils/objectData";
import { highlightText } from "utils/textHighlight";
import { useIsTruncated } from "utils/truncatedText";

interface PropertyRowProps {
    propertyName: string;
    propertyValue: string;
    searchTerm: string;
    isSelected: boolean;
    onToggleSelection: () => void;
}

const TruncatedCell = ({ content, searchTerm }: { content: string; searchTerm: string }) => {
    const { ref, isTruncated } = useIsTruncated(content);

    // Highlighting matches `content` (decoded). Encoded search terms still filter
    // rows in usePropertyFiltering; they will not paint a highlight here.
    const cellContent = (
        <Table.TextCell
            ref={ref}
            style={{
                display: "block",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "100%",
            }}
        >
            {highlightText(content, searchTerm)}
        </Table.TextCell>
    );

    // Always wrap in Tooltip, but only show content when truncated
    return (
        <Tooltip overlay={isTruncated ? <Tooltip.Content>{content}</Tooltip.Content> : <span></span>} placement="top">
            {cellContent}
        </Tooltip>
    );
};

export function PropertyRow({
    propertyName,
    propertyValue,
    searchTerm,
    isSelected,
    onToggleSelection,
}: PropertyRowProps) {
    // Selecting a property triggers a search for similar objects; disable the checkbox
    // when nothing can answer it so it can't fan out a doomed network call.
    const dbStatus = useDbStatus();
    const displayName = getPropertyDisplayName(propertyName);
    const displayValue = getPropertyDisplayValue(propertyValue);

    return (
        <Table.BodyRow>
            <Table.BodyCell style={{ maxWidth: "200px" }}>
                <Box display="flex" alignItems="center" gap="sm">
                    <Table.Checkbox
                        checked={isSelected}
                        onChange={onToggleSelection}
                        disabled={!dbStatus.isIndexAvailable}
                        aria-label={`Select ${displayName}`}
                    />
                    <TruncatedCell content={displayName} searchTerm={searchTerm} />
                </Box>
            </Table.BodyCell>
            <Table.BodyCell style={{ maxWidth: "250px" }}>
                <TruncatedCell content={displayValue} searchTerm={searchTerm} />
            </Table.BodyCell>
        </Table.BodyRow>
    );
}
